import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Check, Loader2, Users } from "lucide-react";
import users from "./../data/PopularUsers";

const AddAnime = () => {
  const api = "https://anime-quiz-backend.vercel.app/api";
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [togglePersonal, setTogglePersonal] = useState(true);
  const [playWithFriends, setPlayWithFriends] = useState(false);
  const [showFriends, setShowFriends] = useState(false);
  const [friendUsernames, setFriendUsernames] = useState(["", ""]);
  const [playerValidation, setPlayerValidation] = useState({});
  const [validatingPlayer, setValidatingPlayer] = useState(null);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [mergeMode, setMergeMode] = useState("overlap");

  const navigate = useNavigate();

  const MAX_PLAYERS = 4;
  const MIN_PLAYERS = 2;

  const validPlayerCount = Object.values(playerValidation).filter(
    (v) => v.status === "valid",
  ).length;

  const canStartGame = validPlayerCount >= MIN_PLAYERS;

  // --- Single player ---

  const submitUser = async (uname) => {
    console.log("in API");

    if (loading) return;

    if (typeof uname !== "string") {
      console.error("username is not a string:", uname);
      return;
    }
    uname = uname.trim();

    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${api}/user-anime?username=${uname}`);
      if (!res.ok) {
        console.error(
          "Response From the Api was not okay. Status:",
          res.status,
        );
        setError(
          "Issue finding the entered user. Please check the username and try again.",
        );
        setLoading(false);
        return;
      }
      const data = await res.json();
      console.log("Receiving:" + JSON.stringify(data, null, 2));
      navigate("/guesstheanime", {
        state: { userData: data, username: uname },
      });
    } catch (error) {
      console.error("Error with pulling information from user at" + error);
      setError("An error occurred while fetching data. Please try again.");
      setLoading(false);
    }
  };

  // --- Multiplayer helpers ---

  const validateSingleUsername = async (uname, index) => {
    const trimmed = uname.trim();
    if (!trimmed) return;

    setValidatingPlayer(index);
    setPlayerValidation((prev) => ({
      ...prev,
      [index]: { status: "validating" },
    }));

    try {
      const res = await fetch(
        `${api}/user-anime?username=${trimmed}&validate=true`,
      );
      const data = await res.json();

      if (data.valid) {
        setPlayerValidation((prev) => ({
          ...prev,
          [index]: {
            status: "valid",
            username: trimmed,
            animeCount: data.animeCount,
            picture: data.picture,
          },
        }));
      } else {
        setPlayerValidation((prev) => ({
          ...prev,
          [index]: {
            status: "error",
            message: data.error || "User not found",
          },
        }));
      }
    } catch (err) {
      setPlayerValidation((prev) => ({
        ...prev,
        [index]: { status: "error", message: "Validation failed" },
      }));
    }

    setValidatingPlayer(null);
  };

  const fetchPlayerAnimeList = async (username) => {
    const res = await fetch(`${api}/user-anime?username=${username}`);
    if (res.ok) {
      return await res.json();
    }
    return [];
  };

  const addFriendInput = () => {
    if (friendUsernames.length < MAX_PLAYERS) {
      setFriendUsernames([...friendUsernames, ""]);
    }
  };

  const removeFriendInput = (index) => {
    if (friendUsernames.length > MIN_PLAYERS) {
      const newList = friendUsernames.filter((_, i) => i !== index);
      setFriendUsernames(newList);
      setPlayerValidation((prev) => {
        const newValidation = { ...prev };
        delete newValidation[index];
        return newValidation;
      });
    }
  };

  const updateFriendUsername = (index, value) => {
    const newList = [...friendUsernames];
    newList[index] = value;
    setFriendUsernames(newList);
    setPlayerValidation((prev) => ({
      ...prev,
      [index]: { status: "pending" },
    }));
  };

  const validateAllPlayers = async () => {
    const validUsernames = friendUsernames.filter((u) => u.trim() !== "");
    if (validUsernames.length < MIN_PLAYERS) {
      setError(`Please add at least ${MIN_PLAYERS} players`);
      return;
    }
    if (validUsernames.length > MAX_PLAYERS) {
      setError(`Maximum ${MAX_PLAYERS} players allowed`);
      return;
    }

    setError("");
    for (let i = 0; i < friendUsernames.length; i++) {
      if (friendUsernames[i].trim()) {
        await validateSingleUsername(friendUsernames[i], i);
      }
    }
  };

  const fetchAllLists = async () => {
    const validPlayers = Object.values(playerValidation).filter(
      (v) => v.status === "valid",
    );

    if (validPlayers.length < MIN_PLAYERS) {
      setError(`At least ${MIN_PLAYERS} players must have valid lists`);
      return;
    }

    setLoadingFriends(true);
    setError("");

    try {
      const allAnimeData = await Promise.all(
        validPlayers.map(async (p) => {
          const animeList = await fetchPlayerAnimeList(p.username);
          return { username: p.username, anime: animeList };
        }),
      );

      if (allAnimeData.length === 1) {
        navigate("/guesstheanime", {
          state: {
            userData: allAnimeData[0].anime,
            username: allAnimeData[0].username,
          },
        });
      } else if (mergeMode === "overlap") {
        const baseList = allAnimeData[0].anime;
        const overlapping = baseList.filter((anime) =>
          allAnimeData
            .slice(1)
            .every((list) => list.anime.some((a) => a.title === anime.title)),
        );

        if (overlapping.length === 0) {
          setError(
            "No overlapping anime found between these players. Try different usernames.",
          );
          setLoadingFriends(false);
          return;
        }

        if (overlapping.length < 5) {
          setError(
            `Not enough overlapping anime to start a game (found ${overlapping.length}). Try different usernames or switch to Use All mode.`,
          );
          setLoadingFriends(false);
          return;
        }

        const combinedUsernames = allAnimeData
          .map((l) => l.username)
          .join(", ");
        navigate("/guesstheanime", {
          state: { userData: overlapping, username: combinedUsernames },
        });
      } else {
        // "all" mode — merge all lists and deduplicate by title
        const seen = new Set();
        const merged = allAnimeData
          .flatMap((l) => l.anime)
          .filter((anime) => {
            if (seen.has(anime.title)) return false;
            seen.add(anime.title);
            return true;
          });

        if (merged.length < 5) {
          setError(
            `Not enough anime to start a game (found ${merged.length}). Make sure players have completed anime on their lists.`,
          );
          setLoadingFriends(false);
          return;
        }

        const combinedUsernames = allAnimeData
          .map((l) => l.username)
          .join(", ");
        navigate("/guesstheanime", {
          state: { userData: merged, username: combinedUsernames },
        });
      }
    } catch (err) {
      setError("An error occurred while fetching data. Please try again.");
    }

    setLoadingFriends(false);
  };

  // --- Render ---

  return (
    <div
      className="h-[calc(100vh-8vh)] overflow-auto flex flex-col items-center justify-center subtle-pattern"
      style={{ backgroundColor: "var(--md-sys-color-background)" }}
    >
      {/* Multiplayer Lobby */}
      {showFriends ? (
        <div className="container mx-auto px-4 py-8 flex flex-col items-center animate-fade-in-up w-full max-w-2xl">
          <div className="text-center mb-6">
            <h1 className="text-4xl md:text-5xl font-bold text-[var(--md-sys-color-on-background)] mb-3 tracking-tight">
              Multiplayer Lobby
            </h1>
            <p className="text-lg text-[var(--md-sys-color-on-surface-variant)] max-w-md mx-auto">
              Add {MIN_PLAYERS}–{MAX_PLAYERS} players to find overlapping anime
            </p>
          </div>

          <div className="w-full mb-6">
            <div className="flex justify-center gap-2 flex-wrap">
              {[
                { value: "overlap", label: "Only in Both" },
                { value: "all", label: "Use All" },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setMergeMode(value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    mergeMode === value
                      ? "text-[var(--md-sys-color-on-primary)]"
                      : "text-[var(--md-sys-color-on-surface-variant)]"
                  }`}
                  style={{
                    backgroundColor:
                      mergeMode === value
                        ? "var(--md-sys-color-primary)"
                        : "var(--md-sys-color-surface-variant)",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-center text-xs text-[var(--md-sys-color-on-surface-variant)] mt-2 opacity-75">
              {mergeMode === "overlap"
                ? "Only anime that appear in every player's list"
                : "All anime from every player's list combined"}
            </p>
          </div>

          <div
            className="w-full rounded-2xl shadow-2xl p-8 transform transition-all duration-300 card-shine"
            style={{
              backgroundColor: "var(--md-sys-color-surface-container-high)",
            }}
          >
            <div className="space-y-4">
              {friendUsernames.map((uname, index) => {
                const validation = playerValidation[index];
                const isValidating = validatingPlayer === index;

                return (
                  <div key={index} className="flex gap-2 items-center">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={uname}
                        onChange={(e) =>
                          updateFriendUsername(index, e.target.value)
                        }
                        placeholder={`Player ${index + 1} username`}
                        className="w-full px-4 py-3 rounded-xl border-2 border-transparent focus:border-[var(--md-sys-color-primary)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--md-sys-color-surface-container-high)] focus:ring-[var(--md-sys-color-primary)] transition-all duration-300 text-[var(--md-sys-color-on-surface)] placeholder-[var(--md-sys-color-on-surface-variant)] shadow-inner"
                        style={{
                          backgroundColor: "var(--md-sys-color-surface-bright)",
                          borderColor:
                            validation?.status === "valid"
                              ? "var(--md-sys-color-primary)"
                              : validation?.status === "error"
                                ? "var(--md-sys-color-error)"
                                : undefined,
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            validateSingleUsername(uname, index);
                          }
                        }}
                      />
                      {validation?.status === "valid" && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[var(--md-sys-color-primary)]">
                          <Check size={16} />
                          <span className="text-xs">
                            {validation.animeCount || 0} anime
                          </span>
                        </div>
                      )}
                      {isValidating && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2
                            size={16}
                            className="animate-spin text-[var(--md-sys-color-on-surface-variant)]"
                          />
                        </div>
                      )}
                    </div>
                    {friendUsernames.length > MIN_PLAYERS && (
                      <button
                        onClick={() => removeFriendInput(index)}
                        className="px-3 py-2 rounded-xl text-[var(--md-sys-color-error)] hover:bg-[var(--md-sys-color-error-container)] transition-colors"
                        aria-label="Remove player"
                      >
                        <X size={20} />
                      </button>
                    )}
                  </div>
                );
              })}

              {friendUsernames.length < MAX_PLAYERS && (
                <button
                  onClick={addFriendInput}
                  className="w-full py-2 rounded-xl border-2 border-dashed border-[var(--md-sys-color-outline)] text-[var(--md-sys-color-on-surface-variant)] hover:border-[var(--md-sys-color-primary)] hover:text-[var(--md-sys-color-primary)] transition-colors text-sm font-medium"
                >
                  + Add Player
                </button>
              )}

              {error && (
                <p className="text-[var(--md-sys-color-error)] text-sm text-center animate-pulse-soft">
                  {error}
                </p>
              )}

              {validPlayerCount > 0 && (
                <div className="text-center text-sm text-[var(--md-sys-color-on-surface-variant)]">
                  {validPlayerCount}/
                  {friendUsernames.filter((u) => u.trim()).length} players
                  validated
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={validateAllPlayers}
                  disabled={
                    validatingPlayer !== null ||
                    friendUsernames.filter((u) => u.trim()).length < MIN_PLAYERS
                  }
                  className="flex-1 py-3.5 px-4 rounded-xl font-semibold text-[var(--md-sys-color-on-secondary-container)] transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--md-sys-color-surface-container-high)] focus:ring-[var(--md-sys-color-secondary)]"
                  style={{
                    backgroundColor: "var(--md-sys-color-secondary-container)",
                  }}
                >
                  {validatingPlayer !== null ? (
                    <div className="flex items-center justify-center">
                      <Loader2 size={18} className="animate-spin mr-2" />
                      Validating...
                    </div>
                  ) : (
                    "Validate All Players"
                  )}
                </button>

                <button
                  onClick={fetchAllLists}
                  disabled={!canStartGame || loadingFriends}
                  className="flex-1 py-3.5 px-4 rounded-xl font-semibold text-[var(--md-sys-color-on-primary)] transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--md-sys-color-surface-container-high)] focus:ring-[var(--md-sys-color-primary)]"
                  style={{ backgroundColor: "var(--md-sys-color-primary)" }}
                >
                  {loadingFriends ? (
                    <div className="flex items-center justify-center">
                      <Loader2 size={18} className="animate-spin mr-2" />
                      Finding Overlap...
                    </div>
                  ) : canStartGame ? (
                    `Start Game (${validPlayerCount} players)`
                  ) : (
                    "Validate players to start"
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setShowFriends(false);
                setError("");
                setFriendUsernames(["", ""]);
                setPlayerValidation({});
                setMergeMode("overlap");
              }}
              className="text-[var(--md-sys-color-primary)] hover:underline cursor-pointer text-sm font-medium transition-colors"
            >
              Back to Single Player
            </button>
          </div>
        </div>
      ) : togglePersonal ? (
        /* Single Player - Personal List */
        <div className="container mx-auto px-4 py-8 flex flex-col items-center justify-center animate-fade-in-up">
          <div className="text-center mb-2">
            <h1 className="text-4xl md:text-5xl font-bold text-[var(--md-sys-color-on-background)] mb-3 tracking-tight">
              Anime Lookup
            </h1>
            <p className="text-lg text-[var(--md-sys-color-on-surface-variant)] max-w-md mx-auto">
              Find and explore anime collections by username
            </p>
          </div>

          <div
            className="w-full max-w-md rounded-2xl shadow-2xl p-8 transform transition-all duration-300 hover:scale-[1.02] card-shine"
            style={{
              backgroundColor: "var(--md-sys-color-surface-container-high)",
            }}
          >
            <div className="space-y-6">
              <div>
                <label
                  htmlFor="MALUserId"
                  className="block text-sm font-semibold text-[var(--md-sys-color-on-surface)] mb-2"
                >
                  MyAnimeList Username
                </label>
                <input
                  type="text"
                  id="MALUserId"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitUser(username);
                    }
                  }}
                  className="w-full px-4 py-3.5 rounded-xl border-2 border-transparent focus:border-[var(--md-sys-color-primary)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--md-sys-color-surface-container-high)] focus:ring-[var(--md-sys-color-primary)] transition-all duration-300 text-[var(--md-sys-color-on-surface)] placeholder-[var(--md-sys-color-on-surface-variant)] shadow-inner"
                  style={{
                    backgroundColor: "var(--md-sys-color-surface-bright)",
                  }}
                  placeholder="Enter username..."
                  aria-label="MyAnimeList Username Input"
                  aria-required="true"
                />
              </div>

              {error && (
                <p className="text-[var(--md-sys-color-error)] text-sm text-center animate-pulse-soft">
                  {error}
                </p>
              )}

              <button
                onClick={() => submitUser(username)}
                disabled={loading || !username.trim()}
                className="w-full py-3.5 px-4 rounded-xl font-semibold text-[var(--md-sys-color-on-primary)] transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--md-sys-color-surface-container-high)] focus:ring-[var(--md-sys-color-primary)]"
                style={{ backgroundColor: "var(--md-sys-color-primary)" }}
                aria-live="polite"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[var(--md-sys-color-on-primary)] mr-2"></div>
                    Searching...
                  </div>
                ) : (
                  "Find Anime Collection"
                )}
              </button>
            </div>
          </div>
          <div>
            <div className="mt-8 text-center">
              <p className="text-[var(--md-sys-color-on-surface-variant)] text-sm">
                Connect your MyAnimeList account to start the guessing game
              </p>
            </div>
            <div className="mt-3 text-center">
              <p
                className="text-[var(--md-sys-color-primary)] hover:underline cursor-pointer text-sm font-medium transition-colors"
                onClick={() => {
                  setTogglePersonal(false);
                }}
              >
                Dont have a list? Click here!
              </p>
            </div>
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowFriends(true)}
                className="flex items-center gap-2 mx-auto text-[var(--md-sys-color-primary)] hover:underline cursor-pointer text-sm font-medium transition-colors"
              >
                <Users size={16} />
                Play with Friends
              </button>
            </div>
          </div>
        </div>
      ) : playWithFriends ? (
        <></>
      ) : (
        /* Popular Users List */
        <div className="container mx-auto px-3 md:px-4 py-5 md:py-8 flex flex-col items-center w-full h-full animate-fade-in-up">
          <div className="text-center mb-4 md:mb-6">
            <h1 className="text-3xl md:text-5xl font-bold text-[var(--md-sys-color-on-background)] mb-2 md:mb-3 tracking-tight">
              User Search{" "}
            </h1>
            <p className="text-base md:text-lg text-[var(--md-sys-color-on-surface-variant)] max-w-md mx-auto">
              Find and explore anime collections from popular users
            </p>
            <p className="text-sm text-[var(--md-sys-color-on-surface-variant)] mt-2 opacity-75">
              Want to be added? Shoot me a message with your description and MAL
              name.
            </p>
          </div>

          <div
            className="w-full flex-1 min-h-0 rounded-2xl shadow-2xl p-4 md:p-8 transform transition-all duration-300 card-shine flex flex-col"
            style={{
              backgroundColor: "var(--md-sys-color-surface-container-high)",
            }}
          >
            <div className="flex-1 min-h-0 space-y-4 md:space-y-6 flex flex-col">
              {users && users.length !== 0 ? (
                <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-max p-2 md:p-4 gap-3 md:gap-6">
                  {users.map((user, index) => (
                    <div
                      key={index}
                      className="h-28 md:h-32 w-full rounded-xl text-center justify-center flex-col flex items-center py-3 px-3 md:py-4 md:px-4 transition-all duration-300 hover:scale-[1.03] hover:shadow-lg hover:cursor-pointer border"
                      style={{
                        backgroundColor: "var(--md-sys-color-background)",
                        borderColor: "var(--md-sys-color-outline-variant)",
                      }}
                      onClick={(e) => {
                        console.log("Clicked on the DIV");
                        e.preventDefault();
                        submitUser(user.username);
                      }}
                    >
                      <p
                        className="text-lg md:text-2xl font-bold mb-1 md:mb-2"
                        style={{
                          color: "var(--md-sys-color-on-surface) ",
                        }}
                      >
                        {user.username}
                      </p>
                      <p
                        className="text-sm"
                        style={{
                          color: "var(--md-sys-color-on-surface-variant) ",
                        }}
                      >
                        {user.description}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="justify-center items-center flex h-full">
                  <p
                    className="text-lg text-center"
                    style={{
                      color: "var(--md-sys-color-on-surface-variant) ",
                    }}
                  >
                    No existing users. <br /> Interested in becoming one? Let me
                    know.
                  </p>
                </div>
              )}

              {error && (
                <p className="text-[var(--md-sys-color-error)] text-sm text-center animate-pulse-soft">
                  {error}
                </p>
              )}
            </div>
          </div>
          <div>
            <div className="mt-8 text-center">
              <p className="text-[var(--md-sys-color-on-surface-variant)] text-sm">
                Choose from our library of popular animes
              </p>
            </div>
            <div className="mt-3 text-center">
              <p
                className="text-[var(--md-sys-color-primary)] hover:underline cursor-pointer text-sm font-medium transition-colors"
                onClick={() => {
                  setTogglePersonal(true);
                }}
              >
                Want to use your own list? Click here!
              </p>
            </div>
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowFriends(true)}
                className="flex items-center gap-2 mx-auto text-[var(--md-sys-color-primary)] hover:underline cursor-pointer text-sm font-medium transition-colors"
              >
                <Users size={16} />
                Play with Friends
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddAnime;
